import {call, put, takeEvery, takeLatest, select, take, cancelled} from 'redux-saga/effects';
import axios from 'axios';

// Store current AbortController for analyze health report request
let currentAnalyzeHealthReportAbortController: AbortController | null = null;
// Store AbortControllers for cancel consultation requests (keyed by consultation id)
let currentCancelConsultationAbortControllers: Record<string, AbortController> = {};

// Store current CancelToken source for upload additional files request
let currentUploadAdditionalFilesCancelTokenSource: any = null;
import {
  analyzeHealthReportFailure,
  analyzeHealthReportRequest,
  analyzeHealthReportSuccess,
  cancelAnalyzeHealthReportRequest,
  selectSpecialistRequest,
  selectSpecialistSuccess,
  selectSpecialistFailure,
  getSpecialistListRequest,
  getSpecialistListSuccess,
  getSpecialistListFailure,
  consultationPaymentRequest,
  consultationPaymentSuccess,
  consultationPaymentFailure,
  getAllSpecialistsRequest,
  getAllSpecialistsSuccess,
  getAllSpecialistsFailure,
  newConsultationCheckRequest,
  newConsultationCheckSuccess,
  newConsultationCheckFailure,
  getPendingConsultationCaseFailure,
  getPendingConsultationCaseRequest,
  getPendingConsultationCaseSuccess,
  getConsultationListRequest,
  getConsultationListSuccess,
  getConsultationListFailure,
  uploadAdditionalConsultationFilesRequest,
  cancelUploadAdditionalFilesRequest,
  uploadAdditionalConsultationFilesSuccess,
  uploadAdditionalConsultationFilesFailure,
  removeAdditionalConsultationFileRequest,
  removeAdditionalConsultationFileSuccess,
  removeAdditionalConsultationFileFailure,
  addConsultationReportRequest,
  addConsultationReportSuccess,
  addConsultationReportFailure,
  removeConsultationReportRequest,
  removeConsultationReportSuccess,
  removeConsultationReportFailure,
  submitFeedbackAndRatingRequest,
  submitFeedbackAndRatingSuccess,
  submitFeedbackAndRatingFailure,
  cancelCancelConsultationRequest,
  cancelConsultationRequest,
  cancelConsultationSuccess,
  cancelConsultationFailure,
  followUpConsultationRequest,
  followUpConsultationSuccess,
  followUpConsultationFailure,

  // referral consultation
  referralConsultationRequest,
  referralConsultationSuccess,
  referralConsultationFailure,

  // manual category selection
  manualCategorySelectionRequest,
  manualCategorySelectionSuccess,
  manualCategorySelectionFailure,

  // get specialist details
  getSpecialistDetailsRequest,
  getSpecialistDetailsSuccess,
  getSpecialistDetailsFailure,

  // get specialist feedbacks
  getSpecialistFeedbacksRequest,
  getSpecialistFeedbacksSuccess,
  getSpecialistFeedbacksFailure,

  // specialist booking
  specialistBookingRequest,
  specialistBookingSuccess,
  specialistBookingFailure,

  // proceed with ai recommended specialist
  proceedWithAiRecommendedSpecialistRequest,
  proceedWithAiRecommendedSpecialistSuccess,
  proceedWithAiRecommendedSpecialistFailure,

  // assign hospital specialist
  assignHospitalSpecialistRequest,
  assignHospitalSpecialistSuccess,
  assignHospitalSpecialistFailure,

  // get hospital details
  getHospitalDetailsRequest,
  getHospitalDetailsSuccess,
  getHospitalDetailsFailure,

  // get hospital specialists list
  getHospitalSpecialistsListRequest,
  getHospitalSpecialistsListSuccess,
  getHospitalSpecialistsListFailure,

  // select hospital specialist
  selectHospitalSpecialistRequest,
  selectHospitalSpecialistSuccess,
  selectHospitalSpecialistFailure,

  // add consultation query
  addConsultationQueryRequest,
  addConsultationQuerySuccess,
  addConsultationQueryFailure,

  // select specialist and proceed with payment
  selectSpecialistAndProceedWithPaymentSuccess,
  selectSpecialistAndProceedWithPaymentFailure,
  selectSpecialistAndProceedWithPaymentRequest,

  // favourite doctors
  removeFavouriteDoctorRequest,
  removeFavouriteDoctorSuccess,
  removeFavouriteDoctorFailure,

  addFavouriteDoctorRequest,
  addFavouriteDoctorSuccess,
  addFavouriteDoctorFailure,

  // recent cases
  getRecentCasesRequest,
  getRecentCasesSuccess,
  getRecentCasesFailure,

  // utility
  setPatientDashboardMessage,
  updateProgressBarStep,
  updateUploadAdditionalFilesProgressBarStep,
} from '../slices/patientDashboardSlice';

import {
  uploadHealthReport,
  selectSpecialist,
  getConsultationSpecialists,
  getConsultationPayment,
  getPatientSpecialists,
  newConsulationCheck,
  getPendingConsultationCase,
  getConsultationList,
  uploadAdditionalConsultationFiles,
  removeAdditionalConsultationDocument,
  addConsultationReport,
  removeConsultationReport,
  submitFeedbackAndRating,
  cancelConsultation,
  followUpConsultation,
  referralConsultation,
  manualCategorySelection,
  getSpecialistDetails,
  getSpecialistFeedback,
  specialistBooking,
  proceedWithAiRecommendedSpecialist,
  assignHospitalSpecialist,
  getHospitalDetails,
  getHospitalSpecialistsList,
  selectHospitalSpecialist,
  uploadAdditionalConsultationFiles2,
  addConsultationQuery,
  addFavouriteDoctor,
  removeFavouriteDoctor,
  getRecentCases,
} from '@/services/api';
import { patientDashboardCurrentStep } from '../selectors/patientDashboardSelector';
import {COMPLETE_KEY, ERROR_KEY, PROGRESS_KEY, SUCCESS_KEY} from '@/constants/keys';
import {clearPreSignupFilesToken} from '@/utils/common_functions';
import { mergeDeepRight } from 'ramda';

// get pending consultation case saga
export function* conditionalRequestFlow(options: {
  selector: (state: any) => any;
  condition: (value: any) => boolean;
  actionToDispatch: any;
}): Generator {
  const value = yield select(options.selector);

  if (options.condition(value)) {
    yield put(options.actionToDispatch);
  }
}
/**
 * Uploads health report to backend
 * Clears pre-signup files from localStorage after successful upload
 */
function* analyzeHealthReportSaga(action: any): Generator {
  const {channel, abortController} = yield call(uploadHealthReport, action.payload);
  
  // Store abortController globally for cancellation
  currentAnalyzeHealthReportAbortController = abortController;
  
  try {
    while (true) {
      const event: any = yield take(channel);

      if (event.type === PROGRESS_KEY) {
        yield put(updateProgressBarStep(event.payload.data.step));
      }

      if (event.type === COMPLETE_KEY) {
        const response = event.payload;
        // Check if request was cancelled
        if (response.data?.cancelled) {
          yield put(analyzeHealthReportFailure({
            status: 'error',
            status_code: 'E-10001',
            cancelled: true,
          }));
        } else if (response.data.status === SUCCESS_KEY) {
          yield put(analyzeHealthReportSuccess(response.data));
        } else {
          yield put(analyzeHealthReportFailure(response.data));
        }
        break;
      }

      if (event.type === ERROR_KEY) {
        const response = event.payload;
        // Check if request was cancelled
        if (response?.data?.cancelled) {
          yield put(analyzeHealthReportFailure({
            status: 'error',
            status_code: 'E-10001',
            cancelled: true,
          }));
        } else {
          yield put(analyzeHealthReportFailure(event.payload));
        }
        break;
      }
    }
  } catch (error) {
    yield put(analyzeHealthReportFailure({
      status: 'error',
      status_code: 'E-10001',
    }));
  } finally {
    // Clear the abort controller reference
    if (currentAnalyzeHealthReportAbortController === abortController) {
      currentAnalyzeHealthReportAbortController = null;
    }
    // Check if saga was cancelled
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

/**
 * Cancels the in-flight analyze health report request
 */
function* cancelAnalyzeHealthReportSaga(): Generator {
  if (currentAnalyzeHealthReportAbortController && !currentAnalyzeHealthReportAbortController.signal.aborted) {
    currentAnalyzeHealthReportAbortController.abort();
    currentAnalyzeHealthReportAbortController = null;
  }
}

/**
 * Helper function to cancel any ongoing analyze health report request
 * This is a regular function (not a generator) that can be called from sagas
 */
function* cancelOngoingAnalyzeReportSaga(): Generator {
  if (currentAnalyzeHealthReportAbortController && !currentAnalyzeHealthReportAbortController.signal.aborted) {
    currentAnalyzeHealthReportAbortController.abort();
    currentAnalyzeHealthReportAbortController = null;
    // Dispatch cancel action to update Redux state
    yield put(cancelAnalyzeHealthReportRequest());
  }
}

/**
 * Cancels the in-flight upload additional files request
 */
function* cancelUploadAdditionalFilesSaga(): Generator {
  if (currentUploadAdditionalFilesCancelTokenSource) {
    currentUploadAdditionalFilesCancelTokenSource.cancel('Upload cancelled by user');
    currentUploadAdditionalFilesCancelTokenSource = null;
  }
}

function* selectSpecialistSaga(action: any): Generator {
  try {
    const response: any = yield call(selectSpecialist, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(selectSpecialistSuccess(response.data));
    } else {
      yield put(selectSpecialistFailure(response.data));
    }
  } catch (error: any) {
    yield put(selectSpecialistFailure(error.response));
  }
}

function* getSpecialistListSaga(action: any): Generator {
  try {
    const response: any = yield call(getConsultationSpecialists, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getSpecialistListSuccess(response.data));
    } else {
      yield put(getSpecialistListFailure(response.data));
    }
  } catch (error: any) {
    yield put(getSpecialistListFailure(error.response));
  }
}

function* consultationPaymentSaga(action: any): Generator {
  try {
    const response: any = yield call(getConsultationPayment, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      if (response.data.status_code === 'S-10028') {
        // fee payment success
        yield put(setPatientDashboardMessage({status: 'success', statusCode: response.data.status_code}));
      } else {
        yield put(consultationPaymentSuccess(response.data));
      }
    } else {
      yield put(consultationPaymentFailure(response.data));
    }
  } catch (error: any) {
    yield put(consultationPaymentFailure(error.response));
  }
}

function* getAllSpecialistsSaga(action: any): Generator {
  try {
    const response: any = yield call(getPatientSpecialists, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getAllSpecialistsSuccess(response.data));
    } else {
      yield put(getAllSpecialistsFailure(response.data));
    }
  } catch (error: any) {
    yield put(getAllSpecialistsFailure(error.response));
  }
}

function* newConsultationCheckSaga(): Generator {
  try {
    const response: any = yield call(newConsulationCheck);
    if (response.data.status === SUCCESS_KEY) {
      yield put(newConsultationCheckSuccess(response.data));
    } else {
      yield put(newConsultationCheckFailure(response.data));
    }
  } catch (error: any) {
    yield put(newConsultationCheckFailure(error.response));
  }
}

function* getPendingConsultationSaga(): Generator {
  try {
    const response: any = yield call(getPendingConsultationCase);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getPendingConsultationCaseSuccess(response.data));
    } else {
      yield put(getPendingConsultationCaseFailure(response.data));
    }
  } catch (error: any) {
    yield put(getPendingConsultationCaseFailure(error.response));
  }
}

function* getConsultationListSaga(action: any): Generator {
  try {
    const response: any = yield call(getConsultationList, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getConsultationListSuccess(response.data));
    } else {
      yield put(getConsultationListFailure(response.data));
    }
  } catch (error: any) {
    yield put(getConsultationListFailure(error.response));
  }
}

function* uploadAdditionalConsultationFilesSaga(action: any): Generator {
  // Create cancel token source for this request
  const cancelTokenSource = axios.CancelToken.source();
  currentUploadAdditionalFilesCancelTokenSource = cancelTokenSource;
  
  try {
    const response: any = yield call(uploadAdditionalConsultationFiles, action.payload, cancelTokenSource);
    
    // Check if request was cancelled
    if (response.data?.cancelled) {
      yield put(uploadAdditionalConsultationFilesFailure({
        status: 'error',
        status_code: 'E-10001',
        cancelled: true,
        data: {
          id: action.payload.get('id') as number,
          rejected_files: [],
        },
      }));
      return;
    }
    
    if (response.data.status === SUCCESS_KEY) {
      yield put(uploadAdditionalConsultationFilesSuccess(response.data));
    } else {
      // inject ID in the data from payload so that it can be used in the failure action
      // Also preserve rejected_files if present in the response
      response.data.data = {
        id: action.payload.get('id') as number,
        ...(response.data.data || {}),
        // Preserve rejected_files if present in the response data
        rejected_files: response.data.data?.rejected_files || undefined,
      };
      yield put(uploadAdditionalConsultationFilesFailure(response.data));
    }
  } catch (error: any) {
    // Check if request was cancelled
    if (axios.isCancel(error)) {
      yield put(uploadAdditionalConsultationFilesFailure({
        status: 'error',
        status_code: 'E-10001',
        cancelled: true,
        data: {
          id: action.payload.get('id') as number,
          rejected_files: [],
        },
      }));
      return;
    }
    
    // For error responses, also check if rejected_files are present
    if (error?.response?.data?.data?.rejected_files) {
      error.response.data.data = {
        id: action.payload.get('id') as number,
        ...(error.response.data.data || {}),
        rejected_files: error.response.data.data.rejected_files,
      };
    } else {
      error.response.data.data = {
        id: action.payload.get('id') as number,
        ...(error.response.data.data || {}),
      };
    }
    yield put(uploadAdditionalConsultationFilesFailure(error.response));
  } finally {
    // Clear the cancel token source reference
    if (currentUploadAdditionalFilesCancelTokenSource === cancelTokenSource) {
      currentUploadAdditionalFilesCancelTokenSource = null;
    }
    // Check if saga was cancelled
    if (yield cancelled()) {
      cancelTokenSource.cancel('Saga was cancelled');
    }
  }
}

function* uploadAdditionalConsultationFilesSaga2(action: any): Generator {
  const {channel, abortController} = yield call(uploadAdditionalConsultationFiles2, action.payload);
  
  try {
    while (true) {
      const event: any = yield take(channel);

      if (event.type === PROGRESS_KEY) {
        yield put(updateUploadAdditionalFilesProgressBarStep(event.payload.data.step));
      }

      if (event.type === COMPLETE_KEY) {
        const response = event.payload;
        // Check if request was cancelled
        if (response.data?.cancelled) {
          yield put(uploadAdditionalConsultationFilesFailure({
            status: 'error',
            status_code: 'E-10001',
            cancelled: true,
            data: {
              id: action.payload.get('id') as number,
              rejected_files: [],
            },
          }));
        } else if (response.data.status === SUCCESS_KEY) {
          yield put(uploadAdditionalConsultationFilesSuccess(response.data));
        } else {
          // inject ID in the data from payload so that it can be used in the failure action
          // Also preserve rejected_files if present in the response
          response.data.data = {
            id: action.payload.get('id') as number,
            ...(response.data.data || {}),
            // Preserve rejected_files if present in the response data
            rejected_files: response.data.data?.rejected_files || undefined,
          };
          yield put(uploadAdditionalConsultationFilesFailure(response.data));
        }
        break;
      }

      if (event.type === ERROR_KEY) {
        const response = event.payload;
        // Check if request was cancelled
        if (response?.data?.cancelled) {
          yield put(uploadAdditionalConsultationFilesFailure({
            status: 'error',
            status_code: 'E-10001',
            cancelled: true,
            data: {
              id: action.payload.get('id') as number,
              rejected_files: [],
            },
          }));
        } else {
          yield put(uploadAdditionalConsultationFilesFailure(event.payload));
        }
        break;
      }
    }
  } catch (error) {
    yield put(uploadAdditionalConsultationFilesFailure({
      status: 'error',
      status_code: 'E-10001',
      data: {
        id: action.payload.get('id') as number,
        rejected_files: [],
      },
    }));
  } finally {
    // Check if saga was cancelled
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

function* removeAdditionalConsultationFileSaga(action: any): Generator {
  const consultationId = action.payload?.id;
  try {
    // transform to API expected body
    const response: any = yield call(removeAdditionalConsultationDocument, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(removeAdditionalConsultationFileSuccess(response.data));
    } else {
      yield put(removeAdditionalConsultationFileFailure({...response.data, id: consultationId}));
    }
  } catch (error: any) {
    yield put(removeAdditionalConsultationFileFailure({...error.response, id: consultationId}));
  }
}

function* AddConsultationReportSaga(action: any): Generator {
  try {
    // check if the request is for pre-signup
    const preSignup = action.payload.get('pre-signup') ?? false;

    const response: any = yield call(addConsultationReport, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(addConsultationReportSuccess(response.data));
      // if presignup clear the storage
      if (preSignup === 'true') {
        clearPreSignupFilesToken();
      }
    } else {
      yield put(addConsultationReportFailure(response.data));
    }
  } catch (error: any) {
    yield put(addConsultationReportFailure(error.response));
  }
}

function* RemoveConsultationReportSaga(action: any): Generator {
  const fileId = action.payload.id;
  try {
    const response: any = yield call(removeConsultationReport, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(removeConsultationReportSuccess(response.data));
    } else {
      yield put(removeConsultationReportFailure({...response.data, ...{data: {file_id: fileId}}}));
    }
  } catch (error: any) {
    yield put(removeConsultationReportFailure({...error.response, id: fileId}));
  }
}

function* SubmitFeedbackAndRatingSaga(action: any): Generator {
  try {
    const response: any = yield call(submitFeedbackAndRating, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(submitFeedbackAndRatingSuccess(response.data));
    } else {
      yield put(submitFeedbackAndRatingFailure(response.data));
    }
  } catch (error: any) {
    yield put(submitFeedbackAndRatingFailure(error.response));
  }
}

function* CancelConsultationSaga(action: any): Generator {
  const consultationId = action.payload?.id;
  if (consultationId) {
    // Cancel the upload request if it's in progress
    if (currentUploadAdditionalFilesCancelTokenSource) {
      currentUploadAdditionalFilesCancelTokenSource.cancel('Consultation cancelled');
      currentUploadAdditionalFilesCancelTokenSource = null;
    }
    // Clear the upload file meta for this consultation
    yield put(cancelUploadAdditionalFilesRequest({id: consultationId}));
  }
  
  let controller: AbortController | null = null;
  try {
    controller = new AbortController();

    const key = consultationId?.toString();
    if (key) {
      // Abort any previous cancel-consultation request for the same consultation id
      const existing = currentCancelConsultationAbortControllers[key];
      if (existing && !existing.signal.aborted) {
        existing.abort();
      }
      currentCancelConsultationAbortControllers[key] = controller;
    }

    const response: any = yield call(cancelConsultation, action.payload, controller.signal);
    if (response.data.status === SUCCESS_KEY) {
      // Cancel any ongoing upload additional files request for this consultation
      yield put(cancelConsultationSuccess(response.data));
    } else {
      // Pass consultation ID in failure response
      yield put(cancelConsultationFailure({...response.data, data: {...response.data.data, id: consultationId}}));
    }
  } catch (error: any) {
    // Pass consultation ID in error response
    const errorResponse = error.response || {data: {status: 'error', status_code: 'E-10001'}};
    yield put(cancelConsultationFailure({...errorResponse, data: {...errorResponse.data, id: consultationId}}));
  } finally {
    // Clear controller reference
    const key = consultationId?.toString();
    if (key && controller && currentCancelConsultationAbortControllers[key] === controller) {
      delete currentCancelConsultationAbortControllers[key];
    }
  }
}

function* cancelCancelConsultationSaga(action: any): Generator {
  const consultationId = action?.payload?.id;

  if (consultationId !== undefined && consultationId !== null) {
    const key = consultationId.toString();
    const controller = currentCancelConsultationAbortControllers[key];
    if (controller && !controller.signal.aborted) {
      controller.abort();
    }
    delete currentCancelConsultationAbortControllers[key];
    return;
  }

  // If no id is provided, abort all in-flight cancel-consultation requests
  for (const key of Object.keys(currentCancelConsultationAbortControllers)) {
    const controller = currentCancelConsultationAbortControllers[key];
    if (controller && !controller.signal.aborted) {
      controller.abort();
    }
    delete currentCancelConsultationAbortControllers[key];
  }
}


function* FollowUpConsultationSaga(action: any): Generator {
  try {
    const response: any = yield call(followUpConsultation, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      // Cancel any ongoing analyze report request
      yield* cancelOngoingAnalyzeReportSaga();
      yield put(followUpConsultationSuccess(response.data));
      
    } else {
      yield put(followUpConsultationFailure(response.data));
    }
  } catch (error: any) {
    yield put(followUpConsultationFailure(error.response));
  }
}
function* ReferralConsultationSaga(action: any): Generator {
  try {
    const response: any = yield call(referralConsultation, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      // Cancel any ongoing analyze report request
      yield* cancelOngoingAnalyzeReportSaga();
      yield put(referralConsultationSuccess(response.data));

      // chaining to the get pending consultation request saga
      yield conditionalRequestFlow({
        selector: patientDashboardCurrentStep,
        condition: (step) => step === 3,
        actionToDispatch: getPendingConsultationCaseRequest(),
      })

    } else {
      yield put(referralConsultationFailure(response.data));
    }
  } catch (error: any) {
    yield put(referralConsultationFailure(error.response));
  }
}

function* ManualCategorySelectionSaga(action: any): Generator {
  try {
    const response: any = yield call(manualCategorySelection, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(manualCategorySelectionSuccess(response.data));
    } else {
      yield put(manualCategorySelectionFailure(response.data));
    }
  } catch (error: any) {
    yield put(manualCategorySelectionFailure(error.response?.data || error.response));
  }
}

function* GetSpecialistDetailsSaga(action: any): Generator {
  try {
    const response: any = yield call(getSpecialistDetails, action.payload.id);
    if (response.data.status === SUCCESS_KEY) {
      // Include the specialist id in the response for the reducer
      yield put(getSpecialistDetailsSuccess({...response.data, specialistId: action.payload.id}));
    } else {
      yield put(getSpecialistDetailsFailure(response.data));
    }
  } catch (error: any) {
    yield put(getSpecialistDetailsFailure(error.response?.data || error.response));
  }
}

function* GetSpecialistFeedbacksSaga(action: any): Generator {
  try {
    const response: any = yield call(getSpecialistFeedback, action.payload.id);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getSpecialistFeedbacksSuccess(response.data));
    } else {
      yield put(getSpecialistFeedbacksFailure(response.data));
    }
  } catch (error: any) {
    yield put(getSpecialistFeedbacksFailure(error.response?.data || error.response));
  }
}

function* SpecialistBookingSaga(action: any): Generator {
  try {
    const response: any = yield call(specialistBooking, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      // Cancel any ongoing analyze report request
      yield* cancelOngoingAnalyzeReportSaga();
      yield put(specialistBookingSuccess(response.data));
    } else {
      yield put(specialistBookingFailure(response.data));
    }
  } catch (error: any) {
    yield put(specialistBookingFailure(error.response?.data || error.response));
  }
}

function* ProceedWithAiRecommendedSpecialistSaga(action: any): Generator {
  try {
    const response: any = yield call(proceedWithAiRecommendedSpecialist, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(proceedWithAiRecommendedSpecialistSuccess(response.data));
    } else {
      yield put(proceedWithAiRecommendedSpecialistFailure(response.data));
    }
  } catch (error: any) {
    yield put(proceedWithAiRecommendedSpecialistFailure(error.response?.data || error.response));
  }
}

function* AssignHospitalSpecialistSaga(action: any): Generator {
  try {
    const response: any = yield call(assignHospitalSpecialist, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      // Cancel any ongoing analyze report request
      yield* cancelOngoingAnalyzeReportSaga();
      yield put(assignHospitalSpecialistSuccess(response.data));

    } else {
      yield put(assignHospitalSpecialistFailure(response.data));
    }
  } catch (error: any) {
    yield put(assignHospitalSpecialistFailure(error.response?.data || error.response));
  }
}

function* GetHospitalDetailsSaga(action: any): Generator {
  try {
    const response: any = yield call(getHospitalDetails, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getHospitalDetailsSuccess(response.data));
    } else {
      yield put(getHospitalDetailsFailure(response.data));
    }
  } catch (error: any) {
    yield put(getHospitalDetailsFailure(error.response?.data || error.response));
  }
}

function* GetHospitalSpecialistsListSaga(action: any): Generator {
  try {
    const response: any = yield call(getHospitalSpecialistsList, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getHospitalSpecialistsListSuccess(response.data));
    } else {
      yield put(getHospitalSpecialistsListFailure(response.data));
    }
  } catch (error: any) {
    yield put(getHospitalSpecialistsListFailure(error.response?.data || error.response));
  }
}

function* SelectHospitalSpecialistSaga(action: any): Generator {
  try {
    const response: any = yield call(selectHospitalSpecialist, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      // Cancel any ongoing analyze report request
      yield* cancelOngoingAnalyzeReportSaga();
      yield put(selectHospitalSpecialistSuccess(response.data));      
    } else {
      yield put(selectHospitalSpecialistFailure(response.data));
    }
  } catch (error: any) {
    yield put(selectHospitalSpecialistFailure(error.response?.data || error.response));
  }
}

function* AddConsultationQuerySaga(action: any): Generator {
  try {
    const response: any = yield call(addConsultationQuery, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(addConsultationQuerySuccess(response.data));
    } else {
      yield put(addConsultationQueryFailure(response.data));
    }
  } catch (error: any) {
    yield put(addConsultationQueryFailure(error.response?.data || error.response));
  }
}

// this is a combined saga that called both api at the same time
function* SelectSpecialistAndconsultationPaymentSaga(action: any): Generator{
  let success_response_data = null;
  let combined_response = {data: null};

  let consultation_id = action.payload?.id;
  const paymentRequestPayload = {
    id: consultation_id,
    payment_method: 'card',
    redirect_url: window.location.href,
  }
  try {
    const response: any = yield call(selectSpecialist, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      success_response_data = true;
      combined_response = response.data;

    } else {
      yield put(selectSpecialistAndProceedWithPaymentFailure(response.data));
    }
  } catch (error: any) {
    yield put(selectSpecialistAndProceedWithPaymentFailure(error.response));
  }

  
// export interface SelectConsultationSpecialistSuccessResponse extends APIResponse {
//   data: {
//     id: number;
//     specialist_name: string;
//     specialist_id: number;
//     amount: number;
//   };
// }

// export interface ConsultationPaymentRequestPayload {
//   id: number;
//   payment_method: 'card' | 'upi' | 'wallet';
//   redirect_url: string;
// }

  if (success_response_data && consultation_id){
    // try calling next api
    try {
      const payment_response: any = yield call(getConsultationPayment, paymentRequestPayload);
      if (payment_response.data.status === SUCCESS_KEY) {
        combined_response = mergeDeepRight(combined_response, payment_response.data);
        yield put(selectSpecialistAndProceedWithPaymentSuccess(combined_response as any));
      } else {
        yield put(selectSpecialistAndProceedWithPaymentFailure(payment_response.data));
      }
    } catch (payment_error: any) {
      yield put(selectSpecialistAndProceedWithPaymentFailure(payment_error.response));
    }
  }
}

// ==================== Favourite Doctors Sagas ====================
function* AddFavouriteDoctorSaga(action: ReturnType<typeof addFavouriteDoctorRequest>): Generator {
  try {
    const response: any = yield call(addFavouriteDoctor, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(addFavouriteDoctorSuccess({...response.data, data: { specialist_id: action.payload.specialist_id }}));
    } else {
      yield put(addFavouriteDoctorFailure(response.data));
    }
  } catch (error: any) {
    yield put(addFavouriteDoctorFailure(error.response?.data || {status_code: 'E-10000'}));
  }
}

function* RemoveFavouriteDoctorSaga(action: ReturnType<typeof removeFavouriteDoctorRequest>): Generator {
  try {
    const response: any = yield call(removeFavouriteDoctor, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(removeFavouriteDoctorSuccess({...response.data, data: { specialist_id: action.payload.specialist_id }}));
    } else {
      yield put(removeFavouriteDoctorFailure(response.data));
    }
  } catch (error: any) {
    yield put(removeFavouriteDoctorFailure(error.response?.data || {status_code: 'E-10000'}));
  }
}

function* GetRecentCasesSaga(): Generator {
  try {
    const response: any = yield call(getRecentCases);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getRecentCasesSuccess(response.data));
    } else {
      yield put(getRecentCasesFailure(response.data));
    }
  } catch (error: any) {
    yield put(getRecentCasesFailure(error.response?.data || {status_code: 'E-10000'}));
  }
}

export default function* patientDashboardSaga(): Generator {
  yield takeLatest(analyzeHealthReportRequest.type, analyzeHealthReportSaga);
  yield takeLatest(cancelAnalyzeHealthReportRequest.type, cancelAnalyzeHealthReportSaga);
  yield takeLatest(cancelUploadAdditionalFilesRequest.type, cancelUploadAdditionalFilesSaga);
  yield takeLatest(selectSpecialistRequest.type, selectSpecialistSaga);
  yield takeLatest(getSpecialistListRequest.type, getSpecialistListSaga);
  yield takeLatest(consultationPaymentRequest.type, consultationPaymentSaga);
  yield takeLatest(getAllSpecialistsRequest.type, getAllSpecialistsSaga);
  yield takeLatest(newConsultationCheckRequest.type, newConsultationCheckSaga);
  yield takeLatest(getPendingConsultationCaseRequest.type, getPendingConsultationSaga);
  yield takeLatest(getConsultationListRequest.type, getConsultationListSaga);
  yield takeEvery(uploadAdditionalConsultationFilesRequest.type, uploadAdditionalConsultationFilesSaga);
  yield takeEvery(removeAdditionalConsultationFileRequest.type, removeAdditionalConsultationFileSaga);
  yield takeEvery(addConsultationReportRequest.type, AddConsultationReportSaga);
  yield takeEvery(removeConsultationReportRequest.type, RemoveConsultationReportSaga);
  yield takeLatest(submitFeedbackAndRatingRequest.type, SubmitFeedbackAndRatingSaga);
  yield takeLatest(cancelCancelConsultationRequest.type, cancelCancelConsultationSaga);
  yield takeEvery(cancelConsultationRequest.type, CancelConsultationSaga);
  yield takeLatest(followUpConsultationRequest.type, FollowUpConsultationSaga);
  yield takeLatest(referralConsultationRequest.type, ReferralConsultationSaga);
  yield takeLatest(manualCategorySelectionRequest.type, ManualCategorySelectionSaga);
  yield takeLatest(getSpecialistDetailsRequest.type, GetSpecialistDetailsSaga);
  yield takeLatest(getSpecialistFeedbacksRequest.type, GetSpecialistFeedbacksSaga);
  yield takeLatest(specialistBookingRequest.type, SpecialistBookingSaga);
  yield takeLatest(proceedWithAiRecommendedSpecialistRequest.type, ProceedWithAiRecommendedSpecialistSaga);
  yield takeLatest(assignHospitalSpecialistRequest.type, AssignHospitalSpecialistSaga);
  yield takeLatest(getHospitalDetailsRequest.type, GetHospitalDetailsSaga);
  yield takeLatest(getHospitalSpecialistsListRequest.type, GetHospitalSpecialistsListSaga);
  yield takeLatest(selectHospitalSpecialistRequest.type, SelectHospitalSpecialistSaga);
  yield takeLatest(addConsultationQueryRequest.type, AddConsultationQuerySaga);
  yield takeLatest(selectSpecialistAndProceedWithPaymentRequest.type, SelectSpecialistAndconsultationPaymentSaga);
  yield takeLatest(addFavouriteDoctorRequest.type, AddFavouriteDoctorSaga);
  yield takeLatest(removeFavouriteDoctorRequest.type, RemoveFavouriteDoctorSaga);
  yield takeLatest(getRecentCasesRequest.type, GetRecentCasesSaga);
}